import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { brandColors } from "../../App";
import { clientInvoice } from "../../components/Pdf/clientInvoice";
import { exportRequestExcel } from "../../components/Pdf/exportRequestExcel";
import {
  approveRequest,
  ClientTransferList,
  fetchStoreList,
  submitRequest,
  updateRequest,
} from "../../services/inventoryService";
import { RootState } from "../../store";
import { printRequest } from "../../components/Pdf/printPdf";

type RequestStatus = "APPROVED" | "PENDING";

interface TransferItem {
  qty_packed: string | number;
  qty_unpacked: string | number | null;
  status: string;
  created_at: string;
  sending_store: string;
  receiving_store: string;
  product_id: string;
  invoice_no: string;
  store: string;
  unit: string;
  packed: string | number;
  transfer_id: string;
  product: string;
  notes: string;
  first_name: string;
  last_name: string;
  email: string;
  discount_percent: number;
}

interface AggregatedRequest {
  email: string;
  invoice_no: string;
  name: string;
  notes: string;
  date: string;
  transfers: TransferItem[];
}

interface RequestFilter {
  status: RequestStatus;
  page?: number;
  limit?: number;
  totalPages?: number;
}

interface StoreOption {
  label: string;
  value: string;
}

interface SelectedProduct {
  product_id: string;
  name: string;
  qty: number;
  saved: boolean;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(92vw, 520px)",
  maxHeight: "85vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: { xs: 2, sm: 3 },
};

const aggregateByInvoice = (transfers: TransferItem[]): AggregatedRequest[] => {
  const map: Record<string, AggregatedRequest> = {};

  transfers.forEach((item) => {
    const key = item.invoice_no;

    if (!map[key]) {
      map[key] = {
        email: item.email,
        invoice_no: item.invoice_no,
        name: `${item.first_name} ${item.last_name}`.trim(),
        notes: item.notes,
        date: item.created_at.split("T")[0],
        transfers: [],
      };
    }

    map[key].transfers.push(item);
  });

  return Object.values(map);
};

const ClientRequests: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const authUser = useSelector((state: RootState) => state.auth.user);

  const [filter, setFilter] = useState<RequestFilter>({ status: "PENDING" });
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState<AggregatedRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedInvoices, setExpandedInvoices] = useState<string[]>([]);

  // Existing request-submission state is kept so this refactor does not remove
  // any business flow that may be connected elsewhere in the screen later.
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    [],
  );
  const [selectedStore, setSelectedStore] = useState<StoreOption | null>(null);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [open, setOpen] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const userRole = useMemo(() => {
    return user?.role;
  }, [user]);

  const handleClose = () => setOpen(false);

  const toggleInvoice = (invoiceNo: string) => {
    setExpandedInvoices((prev) =>
      prev.includes(invoiceNo)
        ? prev.filter((invoice) => invoice !== invoiceNo)
        : [...prev, invoiceNo],
    );
  };

  const getWarehouse = async () => {
    try {
      const data = await fetchStoreList("WAREHOUSE");
      setStores(
        data.map((item: any) => ({ label: item.name, value: item.store_id })),
      );
    } catch (error) {
      console.error("Error fetching warehouse list:", error);
    }
  };

  const getTransferList = async () => {
    setLoading(true);

    try {
      const clientData = await ClientTransferList(filter, page);
      const aggregatedData = aggregateByInvoice(clientData.data);

      setProducts(aggregatedData);
      setPage(clientData.pagination.page);
      setFilter((prev) => ({
        ...prev,
        page: clientData.pagination.page,
        limit: clientData.pagination.limit,
        totalPages: clientData.pagination.totalPages,
      }));
    } catch (error) {
      console.error("Error fetching transfer requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWarehouse();
  }, []);

  useEffect(() => {
    getTransferList();
  }, [filter.status, page]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    nextPage: number,
  ) => {
    setFilter((prev) => ({ ...prev, page: nextPage }));
    setPage(nextPage);
  };

  const handleChangeStore = (event: SelectChangeEvent) => {
    const store = stores.find((item) => item.value === event.target.value);

    setSelectedStore(store ? { label: store.label, value: store.value } : null);
  };

  const handleSubmit = async () => {
    try {
      const payload = selectedProducts.filter(
        (product) => product.saved && product.qty > 0,
      );
      const response = await submitRequest(payload, selectedStore?.value || "");

      if (response.status === 200) {
        alert("Request submitted successfully");
        await getTransferList();
        setSelectedProducts([]);
        setSelectedStore(null);
        handleClose();
      }
    } catch (error) {
      alert("Something went wrong while submitting the request.");
      console.error("Request submission failed:", error);
    }
  };

  const handleQuantityChange = (
    column: "qty_packed" | "qty_unpacked",
    value: number,
    product: Pick<TransferItem, "product_id" | "transfer_id" | "invoice_no">,
  ) => {
    setProducts((prev) =>
      prev.map((invoiceGroup) =>
        invoiceGroup.invoice_no === product.invoice_no
          ? {
              ...invoiceGroup,
              transfers: invoiceGroup.transfers.map((transfer) =>
                transfer.product_id === product.product_id &&
                transfer.transfer_id === product.transfer_id
                  ? { ...transfer, [column]: value }
                  : transfer,
              ),
            }
          : invoiceGroup,
      ),
    );
  };

  const handleUpdate = async (items: TransferItem[]) => {
    try {
      if (items.length === 0) {
        alert("No items found.");
        return;
      }

      const response = await approveRequest(items);

      if (response) {
        alert("Request updated successfully");
        setProducts((prev) =>
          prev.filter((item) => item.invoice_no !== items[0].invoice_no),
        );
        setSelectedProducts([]);
        setSelectedStore(null);
        handleClose();
      }
    } catch (error) {
      alert("Something went wrong while updating the request.");
      console.error("Request update failed:", error);
    }
  };

  const handleUpdateProduct = async (item: TransferItem) => {
    try {
      const response = await updateRequest(item);

      if (response) {
        alert("Product updated successfully");

        // Products are nested inside an aggregated invoice, so update the
        // matching transfer instead of looking for product_id on the invoice.
        setProducts((prev) =>
          prev.map((invoiceGroup) =>
            invoiceGroup.invoice_no === item.invoice_no
              ? {
                  ...invoiceGroup,
                  transfers: invoiceGroup.transfers.map((transfer) =>
                    transfer.product_id === item.product_id &&
                    transfer.transfer_id === item.transfer_id
                      ? {
                          ...transfer,
                          qty_packed: response.qty_packed,
                          qty_unpacked: response.qty_unpacked,
                        }
                      : transfer,
                  ),
                }
              : invoiceGroup,
          ),
        );
      }
    } catch (error) {
      alert("Something went wrong while updating the product.");
      console.error("Product update failed:", error);
    }
  };

  function getCostMsrpAccess() {
    // allow Admin and SuperAdmin only
    return (
      userRole?.toLocaleLowerCase() === "admin" ||
      userRole?.toLocaleLowerCase() === "superadmin"
    );
  }

  const handleExportExcel = async (
    transfers: TransferItem[],
    notes: string,
  ) => {
    const showCostMsrp = getCostMsrpAccess();
    const showDiscount = getCostMsrpAccess();
    const discount_percent = transfers[0]?.discount_percent || 0;
    const showCost = await exportRequestExcel(
      transfers,
      showCostMsrp,
      showCostMsrp,
      showDiscount,
      notes,
      discount_percent,
    );
  };

  const handlePrint = async (transfers: TransferItem[], notes: string) => {
    try {
      await clientInvoice(
        transfers,
        transfers[0]?.discount_percent || 0,
        notes,
      );
    } catch (error) {
      alert("Something went wrong while generating the invoice.");
      console.error("Invoice generation failed:", error);
    }
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    const status = event.target.value as RequestStatus;

    setFilter((prev) => ({ ...prev, status, page: 1 }));
    setPage(1);
    setExpandedInvoices([]);
  };

  const renderStatusChip = (status?: string) => (
    <Chip
      label={status || filter.status}
      size="small"
      color={(status || filter.status) === "APPROVED" ? "success" : "warning"}
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );

  const renderDesktopProducts = (request: AggregatedRequest) => (
    <Box sx={{ p: 2, bgcolor: "grey.50" }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, overflow: "hidden" }}
      >
        <Table size="small" aria-label={`Products for ${request.invoice_no}`}>
          <TableHead>
            <TableRow>
              {[
                "Product",
                "Packed",
                "Packed Qty",
                "Unpacked Qty",
                "Action",
              ].map((heading) => (
                <TableCell
                  key={heading}
                  align={heading === "Product" ? "left" : "center"}
                  sx={{
                    bgcolor: brandColors.header,
                    color: "common.white",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {request.transfers.map((product) => {
              const isPending = product.status === "PENDING";

              return (
                <TableRow key={product.transfer_id} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Typography variant="body2">{product.product}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {product.packed} {product.unit}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={product.qty_packed}
                      disabled={!isPending}
                      onChange={(event) =>
                        handleQuantityChange(
                          "qty_packed",
                          Number(event.target.value) || 0,
                          product,
                        )
                      }
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{
                        width: 90,
                        "& .MuiInputBase-input": {
                          py: 0,
                          textAlign: "center",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={product.qty_unpacked ?? 0}
                      disabled={!isPending}
                      onChange={(event) =>
                        handleQuantityChange(
                          "qty_unpacked",
                          Number(event.target.value) || 0,
                          product,
                        )
                      }
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{
                        width: 90,
                        "& .MuiInputBase-input": {
                          py: 0,
                          textAlign: "center",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!isPending}
                      onClick={() => handleUpdateProduct(product)}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                      >
                        Update
                      </Typography>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderDesktopView = () => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "auto",
        scrollbarWidth: "none",
        maxHeight: "calc(100vh - 200px)",
      }}
    >
      <Table stickyHeader size="small" sx={{ minWidth: 1020 }}>
        <TableHead>
          <TableRow>
            {[
              "No",
              "Invoice",
              "Customer",
              "Email",
              "Notes",
              "Date",
              "Items",
              "Status",
              "Actions",
              "",
            ].map((heading) => (
              <TableCell
                key={heading || "expand"}
                align={
                  heading === "Customer" || heading === "Notes"
                    ? "left"
                    : "center"
                }
                sx={{
                  bgcolor: brandColors.header,
                  color: "common.white",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {heading}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((request, index) => {
            const expanded = expandedInvoices.includes(request.invoice_no);
            const requestStatus = request.transfers[0]?.status;

            return (
              <React.Fragment key={request.invoice_no}>
                <TableRow
                  hover
                  onClick={() => toggleInvoice(request.invoice_no)}
                  sx={{ cursor: "pointer", "& > *": { borderBottom: 0 } }}
                >
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">
                    <Stack spacing={0.5} sx={{ alignItems: "center" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {request.invoice_no}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 210 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {request.name || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 210 }}>
                    <Typography variant="caption" color="text.secondary">
                      {request.email || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 220, maxWidth: 320 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {request.notes || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {request.date}
                  </TableCell>
                  <TableCell align="center">
                    {request.transfers.length}
                  </TableCell>
                  <TableCell align="center">
                    {renderStatusChip(requestStatus)}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 210 }}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ alignItems: "center", justifyContent: "center" }}
                    >
                      <Tooltip title="Print invoice">
                        <IconButton
                          size="small"
                          aria-label={`Print ${request.invoice_no}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handlePrint(request.transfers, request.notes);
                          }}
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Export Excel">
                        <IconButton
                          size="small"
                          aria-label={`Export ${request.invoice_no} to Excel`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleExportExcel(request.transfers, request.notes);
                          }}
                        >
                          <BrowserUpdatedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {authUser?.role !== "Client" && (
                        <Button
                          variant="contained"
                          size="small"
                          disabled={requestStatus !== "PENDING"}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleUpdate(request.transfers);
                          }}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          Prepared
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      aria-label={
                        expanded ? "Collapse request" : "Expand request"
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleInvoice(request.invoice_no);
                      }}
                    >
                      {expanded ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ p: 0 }} colSpan={10}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      {renderDesktopProducts(request)}
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileProductCard = (product: TransferItem) => {
    const isPending = product.status === "PENDING";

    return (
      <Paper
        key={product.transfer_id}
        variant="outlined"
        sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {product.product}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Packed: {product.packed} {product.unit}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            <TextField
              label="Packed Qty"
              type="number"
              value={product.qty_packed}
              disabled={!isPending}
              onChange={(event) =>
                handleQuantityChange(
                  "qty_packed",
                  Number(event.target.value) || 0,
                  product,
                )
              }
              size="small"
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label="Unpacked Qty"
              type="number"
              value={product.qty_unpacked ?? 0}
              disabled={!isPending}
              onChange={(event) =>
                handleQuantityChange(
                  "qty_unpacked",
                  Number(event.target.value) || 0,
                  product,
                )
              }
              size="small"
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            disabled={!isPending}
            onClick={() => handleUpdateProduct(product)}
            fullWidth
          >
            Update Product
          </Button>
        </Stack>
      </Paper>
    );
  };

  const renderMobileView = () => (
    <Stack
      spacing={1.5}
      sx={{ overflow: "auto", maxHeight: "calc(100vh - 300px)" }}
    >
      {products.map((request) => {
        const expanded = expandedInvoices.includes(request.invoice_no);
        const requestStatus = request.transfers[0]?.status;

        return (
          <Card
            key={request.invoice_no}
            variant="outlined"
            sx={{ borderRadius: 3, minHeight: 260 }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      Invoice
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, wordBreak: "break-word", color: "#015633", borderRadius:1 }}
                      onClick={() =>
                        handlePrint(request.transfers, request.notes)
                      }
                    >
                      {request.invoice_no}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: "center", flexShrink: 0 }}
                  >
                    {renderStatusChip(requestStatus)}
                    <IconButton
                      size="small"
                      aria-label={
                        expanded ? "Collapse request" : "Expand request"
                      }
                      onClick={() => toggleInvoice(request.invoice_no)}
                    >
                      {expanded ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </Stack>
                </Stack>
                <Divider />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "2fr 1fr", sm: "1fr 1fr" },
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Customer
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {request.name || "-"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {request.email || "-"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {request.date}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "2fr 1fr", sm: "1fr 1fr" },
                    gap: 1.25,
                  }}
                >
                  <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {request.notes || "No notes"}
                    </Typography>
                  </Box>
                  {/* <Typography variant="caption" color="text.secondary">
                    {request.transfers.length} item
                    {request.transfers.length === 1 ? "" : "s"}
                  </Typography> */}

                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: "center" }}
                  >
                    <Tooltip title="Print invoice">
                      <IconButton
                        size="small"
                        aria-label={`Print ${request.invoice_no}`}
                        onClick={() =>
                          handlePrint(request.transfers, request.notes)
                        }
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel">
                      <IconButton
                        size="small"
                        aria-label={`Export ${request.invoice_no} to Excel`}
                        onClick={() =>
                          handleExportExcel(request.transfers, request.notes)
                        }
                      >
                        <BrowserUpdatedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center", justifyContent: "space-between" }}
                ></Stack>

                {authUser?.role !== "Client" && (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={requestStatus !== "PENDING"}
                    onClick={() => handleUpdate(request.transfers)}
                    fullWidth
                  >
                    Mark as Prepared
                  </Button>
                )}

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <Stack
                    spacing={1.25}
                    sx={{ pt: 1.5, borderTop: 1, borderColor: "divider" }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Products
                    </Typography>
                    {request.transfers.map(renderMobileProductCard)}
                  </Stack>
                </Collapse>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: "grey.50", minHeight: "100%" }}>
      <Stack spacing={2}>
        {/* <Paper
          variant="outlined"
          sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Client Transfer Requests
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review invoices, update quantities, print, export, and mark
                requests as prepared.
              </Typography>
            </Box>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 170 } }}
            >
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                value={filter.status}
                label="Status"
                onChange={handleStatusChange}
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper> */}

        {loading ? (
          <Paper
            variant="outlined"
            sx={{
              minHeight: 280,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
            }}
          >
            <CircularProgress size={34} />
          </Paper>
        ) : products.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{ p: 5, textAlign: "center", borderRadius: 3 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              No {filter.status.toLowerCase()} requests found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Try another status or check again later.
            </Typography>
          </Paper>
        ) : isSmallScreen ? (
          renderMobileView()
        ) : (
          renderDesktopView()
        )}

        {!loading && products.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography variant="caption" color="text.secondary">
                Page {page} of {filter.totalPages || 1}
              </Typography>
              <Pagination
                count={filter.totalPages || 1}
                page={page}
                onChange={handlePageChange}
                size={isSmallScreen ? "small" : "medium"}
                siblingCount={isSmallScreen ? 0 : 1}
                boundaryCount={1}
                showFirstButton={!isSmallScreen}
                showLastButton={!isSmallScreen}
              />
              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 170 } }}
              >
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={filter.status}
                  label="Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Paper>
        )}
      </Stack>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="verify-list-title"
        aria-describedby="verify-list-description"
      >
        <Box sx={modalStyle}>
          <Stack spacing={2}>
            <Box>
              <Typography
                id="verify-list-title"
                variant="h6"
                sx={{ fontWeight: 800 }}
              >
                Verify list
              </Typography>
              <Typography
                id="verify-list-description"
                variant="body2"
                color="text.secondary"
              >
                Confirm the selected products and destination warehouse before
                submitting.
              </Typography>
            </Box>

            {stores.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel id="warehouse-label">Warehouse</InputLabel>
                <Select
                  labelId="warehouse-label"
                  value={selectedStore?.value || ""}
                  label="Warehouse"
                  onChange={handleChangeStore}
                >
                  {stores.map((store) => (
                    <MenuItem key={store.value} value={store.value}>
                      {store.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        bgcolor: brandColors.header,
                        color: "common.white",
                        fontWeight: 700,
                      }}
                    >
                      Product
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: brandColors.header,
                        color: "common.white",
                        fontWeight: 700,
                      }}
                    >
                      Quantity
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedProducts.map((item) => (
                    <TableRow key={item.product_id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{item.qty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!selectedStore || selectedProducts.length === 0}
              fullWidth={isSmallScreen}
              sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}
            >
              Submit
            </Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default ClientRequests;
